/**
 * クッキーAPIサービス（完全版・改修済み）
 * 
 * バックエンドAPIを使用してクッキー操作を行う
 * CSRF対応: csrfTokenService.executeWithCsrfを使用
 */
import { csrfTokenService } from './csrfTokenService';

const API_BASE_URL = '/api/cookies';

/**
 * カスタムエラークラス
 */
class CookieApiError extends Error {
  constructor(message, status, errorCode) {
    super(message);
    this.name = 'CookieApiError';
    this.status = status;
    this.errorCode = errorCode;
  }
}

/**
 * CSRFトークンを取得（内部使用・レガシー互換性のため残存）
 * @private
 */
const getCsrfToken = async () => {
  return csrfTokenService.getTokenForRequest();
};

/**
 * バックエンドAPIを使用してクッキーを取得
 * @param {string} key - クッキーのキー
 * @returns {Promise<string|null>} クッキーの値またはnull
 */
export const getCookie = async (key) => {
  try {
    const response = await fetch(`${API_BASE_URL}/get?key=${encodeURIComponent(key)}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }

      const errorData = await response.json().catch(() => ({}));
      throw new CookieApiError(
        errorData.message || 'クッキーの取得に失敗しました',
        response.status,
        errorData.errorCode
      );
    }

    const data = await response.json();
    return data.value || null;
  } catch (error) {
    console.error(`[COOKIE API] Failed to get cookie: ${key}`, error);
    if (error instanceof CookieApiError) {
      throw error;
    }

    throw new CookieApiError(
      'クッキーの取得中にエラーが発生しました',
      500,
      'INTERNAL_ERROR'
    );
  }
};

/**
 * バックエンドAPIを使用してクッキーを保存（改修版）
 * @param {string} key - クッキーのキー
 * @param {string} value - クッキーの値
 * @returns {Promise<boolean>} 成功時true
 */
export const saveCookie = async (key, value) => {
  try {
    return await csrfTokenService.executeWithCsrf(async (csrfToken) => {
      const response = await fetch(`${API_BASE_URL}/save`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken
        },
        body: JSON.stringify({ key, value })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new CookieApiError(
          errorData.message || 'クッキーの保存に失敗しました',
          response.status,
          errorData.errorCode
        );
      }
      return true;
    });
  } catch (error) {
    console.error(`[COOKIE API] Failed to save cookie: ${key}`, error);
    if (error instanceof CookieApiError) {
      throw error;
    }
    throw new CookieApiError(
      'クッキーの保存中にエラーが発生しました',
      500,
      'INTERNAL_ERROR'
    );
  }
};

/**
 * バックエンドAPIを使用してクッキーを削除（改修版）
 * @param {string} key - クッキーのキー
 * @returns {Promise<boolean>} 成功時true
 */
export const clearCookie = async (key) => {
  try {
    return await csrfTokenService.executeWithCsrf(async (csrfToken) => {
      const response = await fetch(`${API_BASE_URL}/clear`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken
        },
        body: JSON.stringify({ key })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new CookieApiError(
          errorData.message || 'クッキーの削除に失敗しました',
          response.status,
          errorData.errorCode
        );
      }
      return true;
    });
  } catch (error) {
    console.error(`[COOKIE API] Failed to clear cookie: ${key}`, error);
    if (error instanceof CookieApiError) {
      throw error;
    }
    throw new CookieApiError(
      'クッキーの削除中にエラーが発生しました',
      500,
      'INTERNAL_ERROR'
    );
  }
};

/**
 * 複数のクッキーを一括保存（改修版）
 * @param {Object} cookieData - { key: value } 形式のオブジェクト
 * @returns {Promise<Object>} { success: boolean, errors: Array }
 */
export const saveMultipleCookies = async (cookieData) => {
  const results = {
    success: true,
    errors: []
  };

  // 順次処理（csrfTokenServiceが内部でキュー管理）
  for (const [key, value] of Object.entries(cookieData)) {
    try {
      await saveCookie(key, value);
    } catch (error) {
      results.success = false;
      results.errors.push({
        key,
        error: error.message
      });
    }
  }

  return results;
};

/**
 * 複数のクッキーを一括削除（改修版）
 * @param {string[]} keys - クッキーキーの配列
 * @returns {Promise<Object>} { success: boolean, errors: Array }
 */
export const clearMultipleCookies = async (keys) => {
  const results = {
    success: true,
    errors: []
  };

  // 順次処理（csrfTokenServiceが内部でキュー管理）
  for (const key of keys) {
    try {
      await clearCookie(key);
    } catch (error) {
      results.success = false;
      results.errors.push({
        key,
        error: error.message
      });
    }
  }

  return results;
};

/**
 * JSONオブジェクトをクッキーに保存
 * @param {string} key - クッキーのキー
 * @param {Object} obj - 保存するオブジェクト
 * @returns {Promise<boolean>} 成功時true
 */
export const saveJSONCookie = async (key, obj) => {
  try {
    const jsonString = JSON.stringify(obj);
    return await saveCookie(key, jsonString);
  } catch (error) {
    console.error(`[COOKIE API] Failed to save JSON cookie: ${key}`, error);
    throw error;
  }
};

/**
 * JSONオブジェクトをクッキーから取得
 * @param {string} key - クッキーのキー
 * @returns {Promise<Object|null>} パースされたオブジェクト、失敗時null
 */
export const getJSONCookie = async (key) => {
  try {
    const value = await getCookie(key);
    if (!value) return null;
    return JSON.parse(value);
  } catch (error) {
    console.error(`[COOKIE API] Failed to get JSON cookie: ${key}`, error);
    if (error instanceof CookieApiError) {
      throw error;
    }
    return null;
  }
};

/**
 * クッキーAPIサービスのエクスポート
 */
export const cookieApiService = {
  save: saveCookie,
  get: getCookie,
  clear: clearCookie,
  saveMultiple: saveMultipleCookies,
  clearMultiple: clearMultipleCookies,
  saveJSON: saveJSONCookie,
  getJSON: getJSONCookie
};

// レガシー互換性のためのデフォルトエクスポート
const cookieApiServiceDefault = {
  getCookie,
  getCsrfToken
};

export default cookieApiServiceDefault;

// エラークラスもエクスポート
export { CookieApiError };